import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import dealService from '../../../services/dealService';
import productService from '../../../services/productService';

const DealFormModal = ({ deal, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    productId: '',
    title: '',
    description: '',
    discountType: 'percentage',
    discountValue: '',
    originalPrice: '',
    dealPrice: '',
    affiliatePartner: '',
    affiliateUrl: '',
    startDate: '',
    expirationDate: '',
    status: 'active',
    automatedTracking: true
  });

  useEffect(() => {
    loadProducts();
    if (deal) {
      setFormData({
        productId: deal?.productId || '',
        title: deal?.title || '',
        description: deal?.description || '',
        discountType: deal?.discountType || 'percentage',
        discountValue: deal?.discountValue?.toString() || '',
        originalPrice: deal?.originalPrice?.toString() || '',
        dealPrice: deal?.dealPrice?.toString() || '',
        affiliatePartner: deal?.affiliatePartner || '',
        affiliateUrl: deal?.affiliateUrl || '',
        startDate: deal?.startDate ? new Date(deal?.startDate)?.toISOString()?.slice(0, 16) : '',
        expirationDate: deal?.expirationDate ? new Date(deal?.expirationDate)?.toISOString()?.slice(0, 16) : '',
        status: deal?.status || 'active',
        automatedTracking: deal?.automatedTracking ?? true
      });
    }
  }, [deal]);

  const loadProducts = async () => {
    try {
      const data = await productService?.getAllProducts();
      setProducts(data || []);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate deal price when discount changes
    if (field === 'discountValue' || field === 'originalPrice' || field === 'discountType') {
      const original = parseFloat(field === 'originalPrice' ? value : formData?.originalPrice) || 0;
      const discount = parseFloat(field === 'discountValue' ? value : formData?.discountValue) || 0;
      const type = field === 'discountType' ? value : formData?.discountType;
      
      if (original > 0 && discount > 0) {
        const dealPrice = type === 'percentage' 
          ? original * (1 - discount / 100)
          : original - discount;
        setFormData(prev => ({ ...prev, dealPrice: dealPrice?.toFixed(2) }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    
    // Validation
    if (!formData?.productId || !formData?.title || !formData?.discountValue || 
        !formData?.originalPrice || !formData?.dealPrice || !formData?.expirationDate) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      const dealData = {
        productId: formData?.productId,
        title: formData?.title,
        description: formData?.description,
        discountType: formData?.discountType,
        discountValue: parseFloat(formData?.discountValue),
        originalPrice: parseFloat(formData?.originalPrice),
        dealPrice: parseFloat(formData?.dealPrice),
        affiliatePartner: formData?.affiliatePartner,
        affiliateUrl: formData?.affiliateUrl,
        startDate: formData?.startDate ? new Date(formData?.startDate)?.toISOString() : new Date()?.toISOString(),
        expirationDate: new Date(formData?.expirationDate)?.toISOString(),
        status: formData?.status,
        automatedTracking: formData?.automatedTracking
      };

      if (deal) {
        await dealService?.updateDeal(deal?.id, dealData);
      } else {
        await dealService?.createDeal(dealData);
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving deal:', err);
      setError(err?.message || 'Failed to save deal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {deal ? 'Edit Deal' : 'Create New Deal'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product *
                </label>
                <Select
                  value={formData?.productId}
                  onChange={(e) => handleInputChange('productId', e?.target?.value)}
                  required
                >
                  <option value="">Select a product</option>
                  {products?.map(product => (
                    <option key={product?.id} value={product?.id}>
                      {product?.brand} {product?.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deal Title *
                </label>
                <Input
                  type="text"
                  value={formData?.title}
                  onChange={(e) => handleInputChange('title', e?.target?.value)}
                  placeholder="e.g., Black Friday Special - 40% Off"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => handleInputChange('description', e?.target?.value)}
                  placeholder="Describe the deal details..."
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Pricing</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Type *
                </label>
                <Select
                  value={formData?.discountType}
                  onChange={(e) => handleInputChange('discountType', e?.target?.value)}
                  required
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed_amount">Fixed Amount ($)</option>
                  <option value="buy_one_get_one">Buy One Get One</option>
                  <option value="bundle">Bundle Deal</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Value *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData?.discountValue}
                  onChange={(e) => handleInputChange('discountValue', e?.target?.value)}
                  placeholder={formData?.discountType === 'percentage' ? '40' : '100'}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Original Price ($) *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData?.originalPrice}
                  onChange={(e) => handleInputChange('originalPrice', e?.target?.value)}
                  placeholder="599.99"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deal Price ($) *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData?.dealPrice}
                  onChange={(e) => handleInputChange('dealPrice', e?.target?.value)}
                  placeholder="359.99"
                  required
                />
              </div>
            </div>
          </div>

          {/* Affiliate Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Affiliate Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Affiliate Partner
                </label>
                <Input
                  type="text"
                  value={formData?.affiliatePartner}
                  onChange={(e) => handleInputChange('affiliatePartner', e?.target?.value)}
                  placeholder="e.g., Amazon, Best Buy"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Affiliate URL
                </label>
                <Input
                  type="url"
                  value={formData?.affiliateUrl}
                  onChange={(e) => handleInputChange('affiliateUrl', e?.target?.value)}
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Schedule</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <Input
                  type="datetime-local"
                  value={formData?.startDate}
                  onChange={(e) => handleInputChange('startDate', e?.target?.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiration Date *
                </label>
                <Input
                  type="datetime-local"
                  value={formData?.expirationDate}
                  onChange={(e) => handleInputChange('expirationDate', e?.target?.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <Select
                  value={formData?.status}
                  onChange={(e) => handleInputChange('status', e?.target?.value)}
                >
                  <option value="active">Active</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="paused">Paused</option>
                  <option value="expired">Expired</option>
                </Select>
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Input
                    type="checkbox"
                    checked={formData?.automatedTracking}
                    onChange={(e) => handleInputChange('automatedTracking', e?.target?.checked)}
                  />
                  <span className="text-sm text-gray-700">Enable automated price tracking</span>
                </label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              className="flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {deal ? 'Update Deal' : 'Create Deal'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DealFormModal;